import { AttentionSummaryFilterType, filterDeferredGroups, filterTenantRespGroups, GroupedDiffLineItems, GroupedReportLineItems } from '@kaiizen/kai-reports';
import { useMemo } from 'react';
import { AttentionSummaryButtons } from './AttentionSummaryButtons';

const countLineItems = (groupedData: Record<string, unknown[]> | null | undefined): number => {
  if (!groupedData) return 0;
  return Object.values(groupedData).reduce((total, items) => total + items.length, 0);
};

export const AttentionSummaryContainer = ({
  sowData,
  activeFilters,
  onFilterToggle,
  diffScopeLineItems,
}: {
  sowData?: GroupedReportLineItems;
  activeFilters: Set<AttentionSummaryFilterType>;
  onFilterToggle: (filterType: AttentionSummaryFilterType) => void;
  diffScopeLineItems?: GroupedDiffLineItems | null;
}) => {
  const editRequestsCount = useMemo(() => {
    if (!sowData) return 0;
    return Object.values(sowData).reduce((total, items) => {
      const editRequestItems = items.filter(item => item.isEditRequest === true && item.isEditResolution === false);
      return total + editRequestItems.length;
    }, 0);
  }, [sowData]);

  const changeOrdersCount = useMemo(() => {
    return countLineItems(diffScopeLineItems);
  }, [diffScopeLineItems]);

  const unassignedContractorsCount = useMemo(() => {
    // Count line items with "Unassigned" contractor
    if (!sowData) return 0;
    return Object.values(sowData).reduce((total, items) => {
      const unassignedItems = items.filter(item => item.contractorName === 'Unassigned');
      return total + unassignedItems.length;
    }, 0);
  }, [sowData]);

  const deferredSowData = useMemo(
    () => (sowData ? filterDeferredGroups(sowData) : {}),
    [sowData],
  );

  const tenantRespSowData = useMemo(
    () => (sowData ? filterTenantRespGroups(sowData) : {}),
    [sowData],
  );

  const deferredTasksCount = useMemo(() => {
    return countLineItems(deferredSowData);
  }, [deferredSowData]);

  const tenantRespCount = useMemo(() => {
    return countLineItems(tenantRespSowData);
  }, [tenantRespSowData]);

  const outOfStockCount = useMemo(() => {
    if (!sowData) return 0;
    return Object.values(sowData).reduce((total, items) => {
      const outOfStockItems = items.filter(item => item.productExists === false);
      return total + outOfStockItems.length;
    }, 0);
  }, [sowData]);

  const handleEditRequestsClick = () => {
    onFilterToggle('editRequests');
  };

  const handleChangeOrdersFilterClick = () => {
    onFilterToggle('changeOrders');
  };

  const handleUnassignedContractorsFilterClick = () => {
    onFilterToggle('unassignedContractors');
  };

  const handleDeferredTasksFilterClick = () => {
    onFilterToggle('deferredTasks');
  };

  const handleTenantRespFilterClick = () => {
    onFilterToggle('tenantResp');
  };

  const handleOutOfStockFilterClick = () => {
    onFilterToggle('outOfStock');
  };

  return (
    <AttentionSummaryButtons
      editRequestsCount={editRequestsCount}
      changeOrdersCount={changeOrdersCount}
      unassignedContractorsCount={unassignedContractorsCount}
      deferredTasksCount={deferredTasksCount}
      tenantRespCount={tenantRespCount}
      outOfStockCount={outOfStockCount}
      onEditRequestsClick={handleEditRequestsClick}
      onChangeOrdersFilterClick={handleChangeOrdersFilterClick}
      onUnassignedContractorsFilterClick={handleUnassignedContractorsFilterClick}
      onDeferredTasksFilterClick={handleDeferredTasksFilterClick}
      onTenantRespFilterClick={handleTenantRespFilterClick}
      onOutOfStockFilterClick={handleOutOfStockFilterClick}
      isEditRequestsActive={activeFilters.has('editRequests')}
      isChangeOrdersActive={activeFilters.has('changeOrders')}
      isUnassignedContractorsActive={activeFilters.has('unassignedContractors')}
      isDeferredTasksActive={activeFilters.has('deferredTasks')}
      isTenantRespActive={activeFilters.has('tenantResp')}
      isOutOfStockActive={activeFilters.has('outOfStock')}
    />
  );
};
